import React from "react";
import { formatValue } from "@/lib/api";
import { Loader2, Minus, TrendingUp, TrendingDown, Activity, Check } from "lucide-react";
import { VERDICT_COLOR } from "./shared";

function freshnessLabel(cachedAt) {
  if (!cachedAt) return null;
  const secs = Date.now() / 1000 - cachedAt;
  if (secs < 90) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function PickSimTab({ pickProj }) {
  if (pickProj === null) {
    return (
      <div className="card-bordered p-12 flex flex-col items-center gap-3">
        <Loader2 className="w-6 h-6 spin text-[var(--accent)]" />
        <div className="text-sm text-[var(--text-3)]">Running {(10000).toLocaleString()} season simulations off live weekly projections…</div>
      </div>
    );
  }
  if ((pickProj.teams || []).length === 0) {
    return <div className="card-bordered p-8 text-center text-sm text-[var(--text-3)]">Not enough projection data to simulate this league yet.</div>;
  }
  const fresh = freshnessLabel(pickProj.projections?.cached_at);
  return (
    <>
      <div className="text-sm text-[var(--text-2)] mb-2">
        Future picks priced like futures contracts — {pickProj.sims?.toLocaleString()} Monte-Carlo season sims off real weekly projections predict where each team's {pickProj.season} 1st actually lands. <span className="text-[var(--text-3)]">Buy the team the market underrates; sell the contender's late 1st.</span>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-3)] font-mono mb-3" data-testid="picksim-freshness">
        <Activity className="w-3 h-3 text-[var(--accent)]" />
        SIM INPUTS: LIVE WEEKLY PROJECTIONS{pickProj.projections?.players_loaded ? ` · ${pickProj.projections.players_loaded.toLocaleString()} PLAYERS` : ""}{fresh ? ` · UPDATED ${fresh.toUpperCase()}` : ""}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {pickProj.teams.map(t => (
          <div key={t.roster_id} className="card-bordered p-5" style={{ borderLeft: `2px solid ${VERDICT_COLOR[t.verdict]}` }} data-testid={`picksim-${t.roster_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display font-bold text-lg leading-tight flex items-center gap-2">
                  {t.manager}
                  {t.i_own && <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded-sm" style={{ background: "#7DD3FC", color: "#000" }} data-testid={`picksim-own-${t.roster_id}`}>YOU OWN THIS</span>}
                </div>
                <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mt-0.5">{t.expected_wins}–{(t.games - t.expected_wins).toFixed(1)} PROJ · {t.playoff_pct}% PLAYOFFS</div>
                {t.held_by && (
                  <div className="text-[10px] text-[var(--text-3)] mt-0.5" data-testid={`picksim-held-${t.roster_id}`}>
                    Held by <span className={t.i_own ? "text-[#7DD3FC]" : "text-[var(--text-2)]"}>{t.i_own ? "you" : t.held_by}</span>{t.is_traded ? " (traded pick)" : ""}
                  </div>
                )}
              </div>
              <div className="text-[10px] tracking-widest uppercase font-mono px-2 py-1 rounded-sm border inline-flex items-center gap-1"
                style={{ color: VERDICT_COLOR[t.verdict], borderColor: VERDICT_COLOR[t.verdict] }} data-testid={`picksim-verdict-${t.roster_id}`}>
                {t.verdict === "BUY" ? <TrendingUp className="w-3 h-3" /> : t.verdict === "SELL" ? <TrendingDown className="w-3 h-3" /> : t.verdict === "OWN" ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />}{t.verdict}
              </div>
            </div>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">PROJECTED {pickProj.season} 1ST</div>
                <div className="font-mono text-2xl text-[var(--accent)]" data-testid={`picksim-label-${t.roster_id}`}>{t.pick_label}</div>
                <div className="text-[10px] text-[var(--text-3)]">{t.range_probability}% probability · {t.top3_pct}% top-3</div>
              </div>
              <div className="text-right">
                <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">SIM VALUE vs MARKET</div>
                <div className="font-mono text-lg">{formatValue(t.pick_value)}</div>
                <div className={`text-[10px] font-mono ${t.value_delta_pct > 0 ? "text-[var(--accent)]" : t.value_delta_pct < 0 ? "text-[var(--negative)]" : "text-[var(--text-3)]"}`}>
                  {t.value_delta_pct > 0 ? "+" : ""}{t.value_delta_pct}% vs {formatValue(t.market_value)}
                </div>
              </div>
            </div>
            <div className="flex gap-1 h-1.5 rounded-sm overflow-hidden mb-1">
              <div style={{ width: `${t.landing.early}%`, background: "var(--accent)" }} title={`Early ${t.landing.early}%`} />
              <div style={{ width: `${t.landing.mid}%`, background: "var(--amber)" }} title={`Mid ${t.landing.mid}%`} />
              <div style={{ width: `${t.landing.late}%`, background: "var(--text-3)" }} title={`Late ${t.landing.late}%`} />
            </div>
            <div className="flex justify-between text-[9px] tracking-widest text-[var(--text-3)] font-mono mb-3">
              <span>EARLY {t.landing.early}%</span><span>MID {t.landing.mid}%</span><span>LATE {t.landing.late}%</span>
            </div>
            <p className="text-xs text-[var(--text-2)] italic">{t.verdict_note}</p>
          </div>
        ))}
      </div>
    </>
  );
}
